import dayjs from "dayjs";

import { mantineColorRamps, tokens } from "~/ui";

/**
 * Pure label and colour logic for the Inbox task row (SC-3700 handoff §3), extracted from
 * `TaskCard.tsx`'s `withChrome={false}` branch so it has proof — a `.tsx` rendering test outside
 * `src/ui` is not collected by jest. The standalone `TaskCard` keeps its own due-date copy.
 */
export type InboxTaskDueTone = "overdue" | "today" | "neutral" | "completed";

export type InboxTaskDue = { label: string; tone: InboxTaskDueTone };

/** Same day-difference logic as `TaskCard`'s own label; only "N Days" / "N Days ago" are reworded. */
export const getInboxTaskDue = (dueDate: Date | null, isCompleted: boolean, now: Date): InboxTaskDue => {
  if (!dueDate) {
    return { label: "No due date", tone: isCompleted ? "completed" : "neutral" };
  }

  const dateDifference = dayjs(dueDate).diff(dayjs(now), "day");
  let label = "Today";
  if (dateDifference === 1) {
    label = "Tomorrow";
  }
  if (dateDifference > 1) {
    label = `In ${dateDifference} days`;
  }
  if (dateDifference === -1) {
    label = "Yesterday";
  }
  if (dateDifference < -1) {
    label = `${Math.abs(dateDifference)} days ago`;
  }

  if (isCompleted) {
    return { label, tone: "completed" };
  }
  if (dateDifference < 0) {
    return { label, tone: "overdue" };
  }
  return { label, tone: dateDifference === 0 ? "today" : "neutral" };
};

export const getInboxTaskDueColors = (tone: InboxTaskDueTone): { background: string; text: string } => {
  if (tone === "overdue") {
    return { background: mantineColorRamps.danger[0], text: tokens.color.content.dangerEmphasis };
  }
  if (tone === "today") {
    return { background: mantineColorRamps.yellow[1], text: tokens.color.content.warning };
  }
  return { background: tokens.color.surface.default, text: tokens.color.content.secondary };
};

/** "You" for the viewer, the full name otherwise, "Not assigned" for no one. */
export const getInboxTaskAssigneeLabel = (assignee: { id: string; name: string } | null, viewerId: string): string => {
  if (!assignee) {
    return "Not assigned";
  }
  return assignee.id === viewerId ? "You" : assignee.name;
};

/** "Created by you" / "Created by Jane Doe" — lowercase "you" because it sits mid-sentence. */
export const getInboxTaskCreatedByLabel = (creator: { id: string; name: string }, viewerId: string): string =>
  `Created by ${creator.id === viewerId ? "you" : creator.name}`;

/** Initials for the assignee avatar when there is no profile image. */
export const getInboxTaskInitials = (name: string): string =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(part => part.charAt(0).toUpperCase())
    .join("");

/** "OPPORTUNITY" -> "Opportunity", "WORK_ORDER" -> "Work order" (handoff §3 sentence case). */
export const formatInboxEntityLabel = (entityType: string): string => {
  const words = entityType.toLowerCase().replace(/_/g, " ");
  return words.charAt(0).toUpperCase() + words.slice(1);
};

/** The "current context" filter chip: "This opportunity", "This work order", … (handoff §1). */
export const getInboxContextChipLabel = (entityType: string): string => `This ${formatInboxEntityLabel(entityType).toLowerCase()}`;
